// src/cockpit4/WorkspaceHeader4.tsx
// ============================================
// Cockpit 4.0 顶部导航栏
//
// 设计：
//   - 左侧：XEAOS Logo + 版本徽标
//   - 中部：9 个 Workspace Tab（图标 + 名称，当前 Tab 高亮霓虹蓝下划线）
//   - 右侧：Hermes 呼出按钮 + 系统状态指示
//
// 风格：参考 Linear / Raycast 顶部导航条
// ============================================

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Crown,
  Megaphone,
  Users,
  BookOpen,
  Store,
  Wallet,
  Sparkles,
  Crosshair,
  Settings,
  PanelRightClose,
  PanelRightOpen,
  Factory,
  Clapperboard,
  LogOut,
  UserCircle,
  Rocket,
  Radar,
  TrendingUp,
  BrainCircuit,
  ZoomIn,
  type LucideIcon,
} from "lucide-react";
import { useCockpit4Store, isWorkspaceAllowed, type WorkspaceId } from "./store";
import { useUserStore } from "../stores/userStore";
import LeadNotificationBell from "./components/LeadNotificationBell";

// ============================================
// 9 个 Workspace 配置（顺序即显示顺序）
// ============================================

interface WorkspaceTabConfig {
  id: WorkspaceId;
  label: string;
  icon: LucideIcon;
  /** 强调色 */
  color: string;
  /** Phase3 P1-4：所属业务分组（用于顶部导航视觉分组） */
  group?: BizGroupId;
}

const TABS: WorkspaceTabConfig[] = [
  { id: "ceo", label: "总控台", icon: Crown, color: "#F59E0B", group: "main" },
  { id: "geodso", label: "GEO+DSO", icon: Radar, color: "#22D3EE", group: "main" },
  { id: "marketing", label: "营销中心", icon: Megaphone, color: "#EC4899", group: "main" },
  { id: "media_studio", label: "内容工厂", icon: Factory, color: "#A855F7", group: "main" },
  { id: "media_workbench", label: "自媒体", icon: Clapperboard, color: "#F472B6", group: "main" },
  { id: "crm", label: "客户中心", icon: Users, color: "#3B82F6", group: "main" },
  { id: "finance", label: "财务中心", icon: Wallet, color: "#10B981", group: "main" },
  { id: "knowledge", label: "知识中心", icon: BookOpen, color: "#22D3EE", group: "brain" },
  { id: "projects", label: "AI 超市", icon: Store, color: "#A855F7", group: "brain" },
  { id: "workflow", label: "经营中心", icon: Crosshair, color: "#06B6D4", group: "boss" },
  { id: "boss_view", label: "老板视图", icon: Rocket, color: "#F59E0B", group: "boss" },
  { id: "settings", label: "设置", icon: Settings, color: "#9CA3AF", group: "system" },
];

// ============================================
// Phase3 P1-4：顶部导航业务分组（视觉分隔，不删功能）
// 分组顺序 = 显示顺序；组间以细分隔线 + 灰色组标题区分
// ============================================
type BizGroupId = "main" | "brain" | "boss" | "system";

interface BizGroup {
  id: BizGroupId;
  label: string;
  icon: LucideIcon;
  color: string;
}

const BIZ_GROUPS: BizGroup[] = [
  { id: "main", label: "经营主线", icon: TrendingUp, color: "#34D399" },
  { id: "brain", label: "知识与智能引擎", icon: BrainCircuit, color: "#A78BFA" },
  { id: "boss", label: "管理层专属", icon: Crown, color: "#F59E0B" },
  { id: "system", label: "系统", icon: Settings, color: "#9CA3AF" },
];

/** 按分组聚合当前可见 tabs（返回含空分组的完整列表，供渲染组标题或跳过空组） */
function groupVisibleTabs(tabs: WorkspaceTabConfig[]): { group: BizGroup; tabs: WorkspaceTabConfig[] }[] {
  return BIZ_GROUPS.map((g) => ({
    group: g,
    tabs: tabs.filter((t) => t.group === g.id),
  }));
}

// ============================================
// v6.x Phase 1：角色驱动的导航可见性（页面级裁剪，服务端同权校验 + 前端一致收口）。
// 可见性规则统一收敛在 store.ts 的 isWorkspaceAllowed，此处只做映射，避免口径漂移。
//   - super_admin：全量；guest：全览（数据由遮罩保护）
//   - employee+content：总控台/营销/内容工厂；employee+sales：总控台/客户中心
//   - employee 未设 sub_role：兼容旧行为（仅隐藏财务）
// ============================================
export function getVisibleTabs(
  role?: string,
  sub_role?: string
): WorkspaceTabConfig[] {
  return TABS.filter((t) => isWorkspaceAllowed(role, sub_role, t.id));
}

// ============================================
// 主组件
// ============================================

export default function WorkspaceHeader4() {
  const currentWorkspace = useCockpit4Store((s) => s.currentWorkspace);
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const hermesDrawerOpen = useCockpit4Store((s) => s.hermesDrawerOpen);
  const toggleHermesDrawer = useCockpit4Store((s) => s.toggleHermesDrawer);
  const currentUser = useUserStore((s) => s.currentUser);

  // v6.x Phase 1：按角色(细分)裁剪导航；guest 仍全览（数据由遮罩保护）
  const isGuest = currentUser?.role === "guest";
  const visibleTabs = getVisibleTabs(
    currentUser?.role,
    currentUser?.role === "employee" ? currentUser?.sub_role : undefined
  );

  return (
    <header className="relative flex items-center h-14 px-4 border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl z-40">
      {/* 顶部光带（霓虹蓝） */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent pointer-events-none" />

      {/* ============ 左侧：Logo ============ */}
      <div className="flex items-center gap-2 pr-4 border-r border-white/[0.06]">
        <div className="relative w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center overflow-hidden">
          {/* 内部呼吸光晕 */}
          <motion.div
            className="absolute inset-0 bg-white/20"
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <span className="relative text-[12px] font-bold text-white tracking-tighter">
            X
          </span>
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[13px] font-semibold text-white tracking-tight">
            XEAOS
          </span>
          <span className="text-[8px] font-mono text-gray-500 uppercase tracking-widest">
            Cockpit 4.0
          </span>
        </div>
      </div>

      {/* ============ 中部：Workspace Tab（Phase3 P1-4 按业务分组渲染，游客只显示 AI 超市） ============ */}
      <nav className="flex items-center gap-0.5 px-3 flex-1 min-w-0 overflow-x-auto scrollbar-none">
        {groupVisibleTabs(visibleTabs).map(
          ({ group, tabs }, gi) =>
            tabs.length === 0 ? null : (
              <div key={group.id} className="flex items-center gap-0.5">
                {/* 组间分隔线（首组左侧不加） */}
                {gi !== 0 && (
                  <span className="mx-1.5 h-4 w-px bg-white/[0.08] flex-shrink-0" />
                )}
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = currentWorkspace === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setWorkspace(tab.id)}
                      className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all group shrink-0 whitespace-nowrap ${
                        isActive
                          ? "text-white bg-white/[0.04]"
                          : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.02]"
                      }`}
                    >
                      <Icon
                        size={13}
                        style={{ color: isActive ? tab.color : undefined }}
                        className="transition-colors shrink-0"
                      />
                      <span className="tracking-tight whitespace-nowrap">{tab.label}</span>

                      {/* 激活态下划线 + 微妙发光 */}
                      {isActive && (
                        <motion.div
                          layoutId="workspace-tab-indicator"
                          className="absolute -bottom-[1px] left-2 right-2 h-0.5 rounded-full"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${tab.color}, transparent)`,
                            boxShadow: `0 0 8px ${tab.color}`,
                          }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )
        )}
      </nav>

      {/* ============ 右侧：Hermes 呼出 + 用户信息 + 系统状态 ============ */}
      <div className="flex items-center gap-3 pl-3 border-l border-white/[0.06]">
        {/* AI 启动器按钮 (Cmd/Ctrl+K) — v6.39 游客隐藏启动器（防止绕过入口访问页面） */}
        {!isGuest && (
          <button
            onClick={() => {
              // 触发自定义事件，由 Workspace4 监听
              window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: !navigator.platform.includes("Mac") }));
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider bg-white/[0.03] text-gray-400 border border-white/[0.06] hover:text-white hover:border-white/10 transition-all"
            title="AI 启动器 (Ctrl+K)"
          >
            <Rocket size={13} />
            <span className="hidden lg:inline">启动器</span>
            <kbd className="hidden xl:inline text-[8px] text-gray-600 px-1 py-0.5 rounded border border-white/[0.06]">⌘K</kbd>
          </button>
        )}

        {/* 官网留资通知（仅 super_admin） */}
        <LeadNotificationBell />

        {/* 演示模式开关（Phase4-UX）：直播/投屏全屏演示时一键放大全站字号（仅登录用户） */}
        {!isGuest && (
          <DemoModeToggle />
        )}

        {/* 系统状态指示 */}
        <div className="hidden md:flex items-center gap-1.5 text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <span>OPERATIONAL</span>
        </div>

        {/* 用户信息 + 登出 */}
        <UserMenu />

        {/* Hermes 呼出按钮（v6.24 游客隐藏——Hermes 对话消耗 LLM 额度） */}
        {!isGuest && (
          <button
            onClick={toggleHermesDrawer}
            className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider transition-all ${
              hermesDrawerOpen
                ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                : "bg-white/[0.03] text-gray-400 border border-white/[0.06] hover:text-white hover:border-white/10"
            }`}
            title={hermesDrawerOpen ? "收起 Hermes 抽屉" : "呼出 Hermes AI 抽屉"}
          >
            {hermesDrawerOpen ? (
              <PanelRightClose size={13} />
            ) : (
              <PanelRightOpen size={13} />
            )}
            <Sparkles size={12} className={hermesDrawerOpen ? "text-purple-400" : ""} />
            <span className="hidden lg:inline">Hermes</span>
            {/* 抽屉关闭时显示一个呼吸点 */}
            {!hermesDrawerOpen && (
              <motion.span
                className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-purple-400"
                animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </button>
        )}
      </div>
    </header>
  );
}

// ============================================
// 演示模式开关组件（Phase4-UX）
// 直播/投屏全屏演示时，一键给 <html> 加 demo-mode 类 → index.css 恒定放大到 1.42。
// localStorage 记忆，刷新保持；常用于向观众播放大屏文字更醒目。
// ============================================
const DEMO_MODE_KEY = "xeaos_demo_mode";

function DemoModeToggle() {
  // 初始状态直接以 localStorage 为准（页面刚加载时 <html> 尚无 demo-mode 类，
  // 若只读 classList 会导致「已放大但 on=false」脱节，ESC/按钮状态错乱）
  const [on, setOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DEMO_MODE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // 保持 class 与状态 + 存储一致（mount 时也执行一次，恢复上次选择）
  useEffect(() => {
    try {
      document.documentElement.classList.toggle("demo-mode", on);
      localStorage.setItem(DEMO_MODE_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [on]);

  const toggleOff = () => {
    setOn(false);
    try {
      document.documentElement.classList.remove("demo-mode");
      localStorage.setItem(DEMO_MODE_KEY, "0");
    } catch {
      /* ignore */
    }
  };

  // ESC 快捷退出：监听始终绑定，进入 handler 实时判断当前是否演示模式，
  // 避免依赖 on 状态（防止状态未同步时 ESC 失效）
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      let isDemo = false;
      try {
        isDemo = document.documentElement.classList.contains("demo-mode");
      } catch {
        /* ignore */
      }
      if (isDemo) toggleOff();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    if (on) {
      toggleOff();
      return;
    }
    setOn(true);
    try {
      document.documentElement.classList.add("demo-mode");
      localStorage.setItem(DEMO_MODE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <button
      onClick={toggle}
      title={
        on
          ? "演示模式：已放大全站字号（点击或按 Esc 恢复）"
          : "演示模式：直播/投屏时点击放大全站字号"
      }
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider transition-all ${
        on
          ? "bg-amber-500/15 text-amber-300 border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.15)]"
          : "bg-white/[0.03] text-gray-400 border border-white/[0.06] hover:text-white hover:border-white/10"
      }`}
    >
      <ZoomIn size={13} className={on ? "text-amber-300" : ""} />
      <span className="hidden lg:inline">
        {on ? "演示中 · Esc退出" : "演示"}
      </span>
    </button>
  );
}

// ============================================
// 用户菜单组件（显示用户名 + 登出按钮）
// ============================================
function UserMenu() {
  const { currentUser, logout } = useUserStore();

  if (!currentUser) return null;

  const isAdmin = currentUser.role === "super_admin";
  const isGuestUser = currentUser.role === "guest";

  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06]">
      <UserCircle
        size={14}
        className={isAdmin ? "text-amber-400" : "text-blue-400"}
      />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-mono text-gray-300 max-w-[100px] truncate">
          {currentUser.displayName}
        </span>
        <span className="text-[8px] font-mono text-gray-600 uppercase">
          {isAdmin ? "SUPER ADMIN" : isGuestUser ? "GUEST" : "EMPLOYEE"}
        </span>
      </div>
      <button
        onClick={logout}
        title="登出"
        className="ml-1 p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
      >
        <LogOut size={12} />
      </button>
    </div>
  );
}
